import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  EMPLOYMENT_TYPES,
  PAY_CYCLES,
  COMPENSATION_TYPES,
  EMPLOYEE_STATUSES,
  normalizeEmploymentValue,
  getLabel,
} from "@shared/constants/employment";
import { Loader2, Pencil, X } from "lucide-react";

// Termination reasons — mirrors TerminateEmployeeModal until we promote it
// to a shared constant.
const TERMINATION_REASONS = [
  { value: "voluntary_resignation", label: "Voluntary Resignation" },
  { value: "involuntary_termination", label: "Involuntary Termination" },
  { value: "layoff", label: "Layoff / Reduction in Force" },
  { value: "retirement", label: "Retirement" },
  { value: "end_of_contract", label: "End of Contract" },
  { value: "death", label: "Death" },
  { value: "disability", label: "Disability" },
  { value: "job_abandonment", label: "Job Abandonment" },
  { value: "misconduct", label: "Misconduct" },
  { value: "mutual_agreement", label: "Mutual Agreement" },
  { value: "other", label: "Other" },
] as const;

// Mirrors ParsedEmployee — optional/nullable everywhere so partial edits
// don't blow up on missing columns (Lindsey minimal, etc.)
export interface EditableEmployee {
  eid?: string | null;
  firstName?: string;
  lastName?: string;
  middleName?: string | null;
  email?: string | null;
  personalEmail?: string | null;
  phone?: string | null;
  workPhone?: string | null;
  ssn?: string | null;
  dateOfBirth?: string | null;
  hireDate?: string | null;
  terminationDate?: string | null;
  terminationReason?: string | null;
  sex?: "male" | "female" | "other" | "unknown";
  maritalStatus?: string | null;
  tobaccoUser?: "Yes" | "No" | "Cessation Program" | null;
  disabled?: "Yes" | "No" | null;
  employmentType?: string | null;
  employmentStatus?:
    | "active"
    | "inactive"
    | "terminated"
    | "cobra"
    | "leave-of-absence"
    | "retired"
    | "pending_activation"
    | null;
  department?: string | null;
  jobTitle?: string | null;
  scheduledHours?: number | null;
  payCycle?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  county?: string | null;
  country?: string | null;
  compensationAmount?: number | null;
  compensationType?: string | null;
  // Carry-through fields we don't edit but must preserve on Save:
  [k: string]: unknown;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: number;
  employees: EditableEmployee[];
  index: number;
  /** Which staging blob this modal writes to. Defaults to "census" — the
   *  enrollment census table. "roster" writes to rosterData.employees for
   *  roster-only rows surfaced in Step 2. */
  mode?: "census" | "roster";
}

type Text = string | null | undefined;

function str(v: Text): string {
  return v == null ? "" : String(v);
}

export function EditEmployeeModal({
  open,
  onOpenChange,
  jobId,
  employees,
  index,
  mode = "census",
}: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const initial = employees[index] ?? ({} as EditableEmployee);
  const [draft, setDraft] = useState<EditableEmployee>(initial);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    // Reset draft + view mode whenever we open against a new employee
    if (open) {
      setDraft(employees[index] ?? ({} as EditableEmployee));
      setIsEditing(false);
    }
  }, [open, index, employees]);

  const save = useMutation({
    mutationFn: async () => {
      const nextEmployees = employees.map((e, i) => (i === index ? draft : e));
      const res = await apiRequest(
        "PATCH",
        `/api/migration/jobs/${jobId}/${mode}/employees`,
        { employees: nextEmployees },
      );
      return await res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/migration/jobs/${jobId}`] });
      toast({ title: "Employee saved" });
      // Stay open in view mode so the broker sees the persisted record
      setIsEditing(false);
    },
    onError: (err: any) =>
      toast({
        title: "Save failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  function set<K extends keyof EditableEmployee>(
    field: K,
    value: EditableEmployee[K],
  ) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function setStr<K extends keyof EditableEmployee>(field: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      set(field, (v === "" ? null : v) as EditableEmployee[K]);
    };
  }

  const missingEmail = !draft.email?.trim();

  const e = isEditing ? draft : (employees[index] ?? ({} as EditableEmployee));
  const fmtSex = (s: typeof e.sex) =>
    s === "male" ? "Male" : s === "female" ? "Female" : s === "other" ? "Other" : "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col gap-0 p-0">
        <DialogHeader className="border-b p-6 pb-4">
          <DialogTitle>
            {e.firstName} {e.lastName}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Editing the migration staging copy. If census is already confirmed, edits are also written through to the imported employee row."
              : "Full record from the source file. Click Edit below to make changes."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          {/* Identity */}
          <Section title="Identity">
            <Field
              label="First name *"
              editing={isEditing}
              display={str(e.firstName)}
            >
              <Input value={str(draft.firstName)} onChange={setStr("firstName")} />
            </Field>
            <Field label="Middle name" editing={isEditing} display={str(e.middleName)}>
              <Input value={str(draft.middleName)} onChange={setStr("middleName")} />
            </Field>
            <Field label="Last name *" editing={isEditing} display={str(e.lastName)}>
              <Input value={str(draft.lastName)} onChange={setStr("lastName")} />
            </Field>
            <Field label="SSN" editing={isEditing} display={str(e.ssn)}>
              <Input value={str(draft.ssn)} onChange={setStr("ssn")} />
            </Field>
            <Field
              label="Date of birth"
              editing={isEditing}
              display={str(e.dateOfBirth)}
            >
              <Input
                type="date"
                value={str(draft.dateOfBirth)}
                onChange={setStr("dateOfBirth")}
              />
            </Field>
            <Field label="Sex" editing={isEditing} display={fmtSex(e.sex)}>
              <Select
                value={str(draft.sex) || "unknown"}
                onValueChange={(v) => set("sex", v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Marital status"
              editing={isEditing}
              display={str(e.maritalStatus)}
            >
              <Input
                value={str(draft.maritalStatus)}
                onChange={setStr("maritalStatus")}
              />
            </Field>
            <Field
              label="Tobacco user"
              editing={isEditing}
              display={str(e.tobaccoUser)}
            >
              <Select
                value={str(draft.tobaccoUser) || ""}
                onValueChange={(v) => set("tobaccoUser", (v || null) as any)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Cessation Program">Cessation Program</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Disabled" editing={isEditing} display={str(e.disabled)}>
              <Select
                value={str(draft.disabled) || ""}
                onValueChange={(v) => set("disabled", (v || null) as any)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Section>

          {/* Contact */}
          <Section title="Contact">
            <Field
              label="Work email *"
              editing={isEditing}
              display={str(e.email)}
              hint={
                missingEmail
                  ? "Required — census confirm will fail without an email"
                  : undefined
              }
            >
              <Input
                type="email"
                value={str(draft.email)}
                onChange={setStr("email")}
                className={missingEmail ? "border-amber-400 bg-amber-50" : ""}
              />
            </Field>
            <Field
              label="Personal email"
              editing={isEditing}
              display={str(e.personalEmail)}
            >
              <Input
                type="email"
                value={str(draft.personalEmail)}
                onChange={setStr("personalEmail")}
              />
            </Field>
            <Field
              label="Personal phone"
              editing={isEditing}
              display={str(e.phone)}
            >
              <Input value={str(draft.phone)} onChange={setStr("phone")} />
            </Field>
            <Field label="Work phone" editing={isEditing} display={str(e.workPhone)}>
              <Input value={str(draft.workPhone)} onChange={setStr("workPhone")} />
            </Field>
          </Section>

          {/* Home address */}
          <Section title="Home Address">
            <Field
              label="Address line 1"
              full
              editing={isEditing}
              display={str(e.address1)}
            >
              <Input value={str(draft.address1)} onChange={setStr("address1")} />
            </Field>
            <Field
              label="Address line 2"
              full
              editing={isEditing}
              display={str(e.address2)}
            >
              <Input value={str(draft.address2)} onChange={setStr("address2")} />
            </Field>
            <Field label="City" editing={isEditing} display={str(e.city)}>
              <Input value={str(draft.city)} onChange={setStr("city")} />
            </Field>
            <Field label="State" editing={isEditing} display={str(e.state)}>
              <Input value={str(draft.state)} onChange={setStr("state")} />
            </Field>
            <Field label="ZIP" editing={isEditing} display={str(e.zipCode)}>
              <Input value={str(draft.zipCode)} onChange={setStr("zipCode")} />
            </Field>
            <Field label="County" editing={isEditing} display={str(e.county)}>
              <Input value={str(draft.county)} onChange={setStr("county")} />
            </Field>
            <Field label="Country" editing={isEditing} display={str(e.country)}>
              <Input value={str(draft.country)} onChange={setStr("country")} />
            </Field>
          </Section>

          {/* Employment */}
          <Section title="Employment">
            <Field
              label="External ID (source)"
              editing={isEditing}
              display={str(e.eid)}
            >
              <Input value={str(draft.eid)} onChange={setStr("eid")} />
            </Field>
            <Field label="Hire date" editing={isEditing} display={str(e.hireDate)}>
              <Input
                type="date"
                value={str(draft.hireDate)}
                onChange={setStr("hireDate")}
              />
            </Field>
            <Field
              label="Employment type"
              editing={isEditing}
              display={getLabel(EMPLOYMENT_TYPES, e.employmentType) || "—"}
            >
              <Select
                value={normalizeEmploymentValue(draft.employmentType ?? "") || ""}
                onValueChange={(v) => set("employmentType", v as any)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Employment status"
              editing={isEditing}
              display={getLabel(EMPLOYEE_STATUSES, e.employmentStatus) || "—"}
            >
              <Select
                value={str(draft.employmentStatus) || "active"}
                onValueChange={(v) => set("employmentStatus", v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYEE_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {/* Termination fields only when status === terminated */}
            {e.employmentStatus === "terminated" && (
              <>
                <Field
                  label="Termination date"
                  editing={isEditing}
                  display={str(e.terminationDate)}
                >
                  <Input
                    type="date"
                    value={str(draft.terminationDate)}
                    onChange={setStr("terminationDate")}
                  />
                </Field>
                <Field
                  label="Termination reason"
                  editing={isEditing}
                  display={
                    TERMINATION_REASONS.find((r) => r.value === e.terminationReason)
                      ?.label || str(e.terminationReason)
                  }
                >
                  <Select
                    value={str(draft.terminationReason) || ""}
                    onValueChange={(v) =>
                      set("terminationReason", (v || null) as any)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {TERMINATION_REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </>
            )}
            <Field
              label="Department"
              editing={isEditing}
              display={str(e.department)}
            >
              <Input
                value={str(draft.department)}
                onChange={setStr("department")}
              />
            </Field>
            <Field label="Job title" editing={isEditing} display={str(e.jobTitle)}>
              <Input value={str(draft.jobTitle)} onChange={setStr("jobTitle")} />
            </Field>
            <Field
              label="Scheduled hours (per week)"
              editing={isEditing}
              display={e.scheduledHours != null ? String(e.scheduledHours) : ""}
            >
              <Input
                type="number"
                min="0"
                step="0.5"
                value={draft.scheduledHours ?? ""}
                onChange={(ev) =>
                  set(
                    "scheduledHours",
                    ev.target.value === "" ? null : Number(ev.target.value),
                  )
                }
              />
            </Field>
            <Field
              label="Pay cycle"
              editing={isEditing}
              display={getLabel(PAY_CYCLES, e.payCycle) || "—"}
            >
              <Select
                value={normalizeEmploymentValue(draft.payCycle ?? "") || ""}
                onValueChange={(v) => set("payCycle", v as any)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {PAY_CYCLES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </Section>

          {/* Compensation */}
          <Section title="Compensation">
            <Field
              label="Compensation amount"
              editing={isEditing}
              display={
                e.compensationAmount != null ? `$${e.compensationAmount}` : ""
              }
            >
              <Input
                type="number"
                min="0"
                step="0.01"
                value={draft.compensationAmount ?? ""}
                onChange={(ev) =>
                  set(
                    "compensationAmount",
                    ev.target.value === "" ? null : Number(ev.target.value),
                  )
                }
              />
            </Field>
            <Field
              label="Compensation type"
              editing={isEditing}
              display={getLabel(COMPENSATION_TYPES, e.compensationType) || "—"}
            >
              <Select
                value={normalizeEmploymentValue(draft.compensationType ?? "") || ""}
                onValueChange={(v) => set("compensationType", v as any)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {COMPENSATION_TYPES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </Section>
        </div>

        <DialogFooter className="border-t bg-background p-4">
          {isEditing ? (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setIsEditing(false);
                  setDraft(employees[index] ?? ({} as EditableEmployee));
                }}
                disabled={save.isPending}
              >
                <X className="mr-1 h-3 w-3" /> Cancel
              </Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={() => setIsEditing(true)}>
                <Pencil className="mr-1 h-3 w-3" /> Edit
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="border-b pb-1 text-sm font-semibold text-muted-foreground">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({
  label,
  children,
  hint,
  full,
  editing,
  display,
}: {
  label: string;
  children?: React.ReactNode;
  hint?: string;
  full?: boolean;
  editing?: boolean;
  /** Read-only display value used when not editing. */
  display?: React.ReactNode;
}) {
  return (
    <div className={`space-y-1 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {editing ? (
        children
      ) : (
        <div className="text-sm">
          {display !== undefined && display !== null && display !== "" ? (
            display
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      )}
      {hint && editing && <p className="text-xs text-amber-700">{hint}</p>}
    </div>
  );
}
